import { css } from '@emotion/css'
import React, { useEffect, useMemo, useState } from 'react'
import { LegacyGridSpinner } from '../../../../legacy-design-system/legacy-grid'
import { DependencyTreeView } from './dependency-tree-view'
import { ErrorInformationPanel } from './error-information-panel'
import {
  GetCliDependencyTreeData,
  GetCliDependencyTreeReports,
  GetDependencyTreeData,
  GetDependencyTreeReports,
  GetMergedApplications
} from './queries'
import { MissingInformationPanel } from './missing-information-panel'
import {
  DependencyTreeResult,
  DependencyType,
  Report,
  SelectedFilters,
  TreeType
} from './interfaces'

const treeContainer = css`
  padding-left: 35px;
  display: flex;
  flex-flow: column;
  height: 100%;
`

interface Props {
  organizationID: string
  applicationID: string
}

const DependencyTree = ({ organizationID, applicationID }: Props) => {
  const [libraryFilter, setLibraryFilter] = useState(DependencyType.PROD)

  const [selectedFilters, setSelectedFilters] = useState<SelectedFilters>({
    dependencies: ['Production']
  })

  const [selectedApplicationId, setSelectedApplicationId] =
    useState(applicationID)

  const [selectedReport, setSelectedReport] = useState<Report>()

  const {
    data: reportsDataCLI,
    isLoading: isLoadingReportsCLI,
    error: reportsErrorCLI
  } = GetCliDependencyTreeReports(selectedApplicationId, organizationID)

  const {
    data: reportsData,
    isLoading: isLoadingReports,
    error: reportsError
  } = GetDependencyTreeReports(
    selectedApplicationId,
    organizationID,
    (!isLoadingReportsCLI && reportsDataCLI?.length === 0) ||
      reportsErrorCLI !== null
  )

  const { data: mergedApplications, isLoading: isLoadingMergedApplications } =
    GetMergedApplications(applicationID, organizationID)

  const reports: Report[] = useMemo(() => {
    if (reportsDataCLI && reportsDataCLI.length > 0) {
      return reportsDataCLI.map((report) => ({
        appID: selectedApplicationId,
        cliVersion: '',
        reportId: report.reportId,
        snapshotCreatedAt: report.created
      }))
    }

    if (reportsData) {
      return reportsData.reports
    }

    return []
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportsData, reportsDataCLI])

  useEffect(() => {
    setSelectedFilters({
      dependencies: ['Production']
    })
    setLibraryFilter(DependencyType.PROD)

    setSelectedReport(undefined)
  }, [selectedApplicationId])

  useEffect(() => {
    if (
      reports &&
      reports.length > 0 &&
      !isLoadingReports &&
      !isLoadingReportsCLI
    ) {
      setSelectedReport(reports[0])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportsData, reportsDataCLI])

  const {
    data: legacyTreeData,
    error: treeDataError,
    isLoading: isTreeDataLoading
  } = GetDependencyTreeData(
    selectedReport?.appID,
    organizationID,
    selectedReport?.reportId,
    libraryFilter,
    !isLoadingReports &&
      reportsData !== undefined &&
      reportsData?.reports.length > 0 &&
      Number.isInteger(selectedReport?.reportId) &&
      selectedReport !== undefined &&
      selectedReport.reportId !== undefined &&
      selectedReport.appID !== undefined
  )

  const {
    data: treeDataCli,
    error: treeDataErrorCli,
    isLoading: isTreeDataLoadingCli
  } = GetCliDependencyTreeData(
    selectedApplicationId,
    organizationID,
    selectedReport?.reportId,
    libraryFilter,
    !isLoadingReports &&
      reportsDataCLI !== undefined &&
      reportsDataCLI.length > 0 &&
      !Number.isInteger(selectedReport?.reportId) &&
      selectedReport !== undefined &&
      selectedReport.reportId !== undefined &&
      selectedReport.appID !== undefined
  )

  const treeType: TreeType = useMemo(
    () => (reportsDataCLI && reportsDataCLI.length > 0 ? 'CLI' : 'LegacyCLI'),
    [reportsDataCLI]
  )

  const error = useMemo(() => {
    if (reportsError && reportsErrorCLI) {
      return reportsError
    }
    if (treeDataError && treeDataErrorCli) {
      return treeDataError
    }
  }, [reportsError, treeDataError, reportsErrorCLI, treeDataErrorCli])

  const isLoading = useMemo(() => {
    return (
      isLoadingReports ||
      isLoadingReportsCLI ||
      isTreeDataLoadingCli ||
      isTreeDataLoading ||
      isLoadingMergedApplications
    )
  }, [
    isLoadingReports,
    isLoadingReportsCLI,
    isTreeDataLoading,
    isTreeDataLoadingCli,
    isLoadingMergedApplications
  ])

  const treeData: DependencyTreeResult | undefined = useMemo(() => {
    if (treeDataCli) {
      return { dependencyTree: treeDataCli, vulnerabilities: [] }
    }

    if (legacyTreeData) {
      return legacyTreeData
    }

    return undefined
  }, [legacyTreeData, treeDataCli])

  return (
    <div className={treeContainer}>
      {isLoading && <LegacyGridSpinner />}
      {error && <ErrorInformationPanel error={error} />}
      {reports.length > 0 && treeData && selectedReport && !isLoading && (
        <DependencyTreeView
          treeType={treeType}
          treeData={treeData}
          reports={reports}
          report={selectedReport}
          setSelectedApplicationId={setSelectedApplicationId}
          mergedApplications={mergedApplications}
          selectedApplicationId={selectedApplicationId}
          setLibraryFilter={setLibraryFilter}
          setSelectedReport={setSelectedReport}
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
        />
      )}
      {!isLoading && reports.length === 0 && (
        <MissingInformationPanel
          organizationID={organizationID}
          mergedApplications={mergedApplications}
          selectedApplicationId={selectedApplicationId}
          setSelectedApplicationId={setSelectedApplicationId}
        />
      )}
    </div>
  )
}
export { DependencyTree }
